import type { Hex, SignerConfig, SignerVaultInfo } from '../types/signers';
import { emblemPost } from './http';
import { bytesToHex, normalizeTxForEmblem } from './utils';
import { fetchVaultInfo } from './vault';

export class EmblemWeb3Adapter {
  readonly address: Hex;
  readonly #vaultId: string;
  readonly #config: SignerConfig;

  constructor(address: Hex, vaultId: string, config: SignerConfig) {
    this.address = address;
    this.#vaultId = vaultId;
    this.#config = config;
  }

  async signMessage(message: string | Uint8Array): Promise<Hex> {
    const payload = typeof message === 'string' ? message : bytesToHex(message);
    const data = await emblemPost<{ signerAddress: string; signature: Hex }>(
      '/sign-eth-message',
      { vaultId: this.#vaultId, message: payload },
      this.#config
    );
    return data.signature;
  }

  async signTypedData(
    domain: Record<string, unknown>,
    types: Record<string, unknown>,
    message: Record<string, unknown>
  ): Promise<Hex> {
    const data = await emblemPost<{ signerAddress: string; signature: Hex }>(
      '/sign-typed-message',
      { vaultId: this.#vaultId, domain, types, message },
      this.#config
    );
    return data.signature;
  }

  async signTransaction(tx: Record<string, unknown>): Promise<{ rawTransaction: Hex }> {
    const normalized = normalizeTxForEmblem(tx);
    const resp = await emblemPost<{ signedTransaction: Hex }>(
      '/sign-eth-tx',
      { vaultId: this.#vaultId, transaction: normalized },
      this.#config
    );
    return { rawTransaction: resp.signedTransaction };
  }
}

export async function toWeb3Adapter(
  config: SignerConfig,
  infoOverride?: SignerVaultInfo
): Promise<EmblemWeb3Adapter> {
  const info = infoOverride ?? (await fetchVaultInfo(config));
  return new EmblemWeb3Adapter(info.evmAddress, info.vaultId, config);
}
